import React, { useState, useEffect } from "react";
import { ColumnDef } from "@tanstack/react-table";
import styles from "./styles/tpa.module.css";
import { Toaster } from "../../components/ui/toaster";
import { useToast } from "../../hooks/use-toast";
import DataTable from "../../shared/DataTable";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/Label";
import { Input } from "../../components/ui/input";
import { TpaPageSchema } from "../../Valdations/Schema/group.schema";
import { useGroupForm } from "../../hooks/useGroupForm";
import { isViewer } from "../../interface/RoleHelpers";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../../components/ui/dialog";
import { TpaAxiosInstance } from "../../Network/Tpa";
import { Datum } from "../../interface/Tpa";
import { AxiosError } from "axios";
import FileUpload from "../../UploadFile/Tpa";
import { canRestoreOrHardDelete } from "../../interface/RoleHelpers";
import { AlertTriangle } from "lucide-react";

const EXPORT_ROW_URL = (id: string | number) => `tpas/${id}/export`;

const saveBlobResponse = (response: any, fallbackName: string) => {
  const disposition: string = response.headers?.["content-disposition"] ?? "";
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
  const filename = match ? decodeURIComponent(match[1]) : fallbackName;

  const blob = new Blob([response.data], {
    type: response.headers?.["content-type"] || "application/octet-stream",
  });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
};

const getBlobErrorMessage = async (error: any, fallback: string): Promise<string> => {
  const data = error?.response?.data;
  if (data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text());
      return parsed?.error?.message || parsed?.message || fallback;
    } catch {
      return fallback;
    }
  }
  return data?.error?.message || data?.message || error?.message || fallback;
};

const TpaPage: React.FC = () => {
  // =====================================
  // Main Data State
  // =====================================
  const [allItems, setAllItems] = useState<Datum[]>([]);

  // =====================================
  // Form Input State
  // =====================================
  const [isActive, setIsActive] = useState<boolean>(true);

  // =====================================
  // Pagination State
  // =====================================
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  // =====================================
  // Form Control Helpers
  // =====================================
  const [submitted, setSubmitted] = useState(false);
  const { values, errors, setField, validateAll } = useGroupForm(TpaPageSchema);

  // =====================================
  // Selected / Editing State
  // =====================================
  const [warningDialog, setWarningDialog] = useState<{
    open: boolean;
    type: "delete" | "hardDelete" | null;
    item: Datum | null;
    usageData: any;
    loadingUsage: boolean;
  }>({
    open: false,
    type: null,
    item: null,
    usageData: null,
    loadingUsage: false,
  });
  const [editIndex, setEditIndex] = useState<null | number>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedPayer, setSelectedPayer] = useState<any[]>([]);
  const [payerDialogOpen, setPayerDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<Datum | null>(null);
  const [showAllLogs, setShowAllLogs] = useState(false);

  // =====================================
  // Search (server-side)
  // =====================================
  const [advancedSearchInput, setAdvancedSearchInput] = useState({
    name: "",
    tpaId: "" as string | number,
    isActive: "",
    payers: "",
    isSoftDeleted: "false",
  });
  const [advancedSearch, setAdvancedSearch] = useState({
    name: "",
    tpaId: "" as string | number,
    isActive: "",
    payers: "",
    isSoftDeleted: "false",
  });

  // =====================================
  // Toast / Notification
  // =====================================
  const { toast } = useToast();

  // =====================================
  // Form Utilities
  // =====================================
  const clear = () => {
    setSubmitted(false);
    setField("name", "");
    setField("tpaId", "");
    setIsActive(true);
  };

  const handleAdd = () => {
    setEditIndex(null);
    setField("name", "");
    setField("tpaId", "");
    setIsDialogOpen(true);
  };

  // =====================================
  // Error Handling
  // =====================================
  const getApiErrorMessage = (error: any): string => {
    if (!error) return "Unknown error";
    if (error.response?.data?.error?.message) {
      return error.response.data.error.message;
    }
    if (error.response?.data?.message) {
      return error.response.data.message;
    }
    if (error.message) {
      return error.message;
    }
    return "An unexpected error occurred.";
  };

  // =====================================
  // Dialog Control Functions
  // =====================================
  const handleOpenDialog = async (item: Datum) => {
    try {
      const response = await TpaAxiosInstance.get(`tpas/${item.id}`);
      const fullData = response.data.data || response.data;
      setSelectedItem(fullData);
      setIsViewDialogOpen(true);
    } catch (error) {
      console.error("Failed to fetch group details:", error);
      toast({
        title: "Error",
        description: "Failed to load group details. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleCloseDialog = () => {
    setIsViewDialogOpen(false);
    setSelectedItem(null);
    setSubmitted(false);
    clear();
    setShowAllLogs(false);
  };

  const buildFilterParams = (): Record<string, any> => {
    const params: Record<string, any> = {};
    if (search) params.name = search;
    if (advancedSearch.name) params.name = advancedSearch.name;
    if (advancedSearch.tpaId !== "") params.tpaId = advancedSearch.tpaId;
    if (advancedSearch.payers) params.payerName = advancedSearch.payers;
    if (advancedSearch.isActive) params.isActive = advancedSearch.isActive === "Active";
    if (advancedSearch.isSoftDeleted !== "") params.isSoftDeleted = advancedSearch.isSoftDeleted === "true";
    return params;
  };

  const hasActiveFilters = Boolean(
    search ||
      advancedSearch.isActive ||
      advancedSearch.isSoftDeleted === "true" ||
      advancedSearch.name ||
      advancedSearch.payers ||
      advancedSearch.tpaId,
  );

  // =====================================
  // Fetch Items (Server-side pagination + search + filters)
  // =====================================
  const fetchItems = async () => {
    try {
      const params: Record<string, any> = {
        page,
        limit,
        ...buildFilterParams(),
      };

      const res = await TpaAxiosInstance.get("tpas", { params });

      const responseData = res.data.data;
      const items = responseData?.items ?? [];

      setAllItems(items);
      setTotalPages(responseData?.totalPages ?? 1);
      setTotalItems(responseData?.total ?? items.length);

      const pages = responseData?.totalPages ?? 1;
      if (page > pages) {
        setPage(Math.max(1, pages));
      }
    } catch (error) {
      console.error(error);
      toast({
        title: "Error",
        description: getApiErrorMessage(error),
        variant: "destructive",
      });
    }
  };

  const getCreatorName = (item: any): string | null => {
    if (!item) return null;
    if (item.createdBy) {
      const match = item.auditLog?.find((log: any) => log.actorId === item.createdBy);
      if (match) return match.actorName;
      return item.createdBy;
    }
    return null;
  };

  // =====================================
  // CRUD Functions
  // =====================================
  const addItem = async (payload: {
    name: string;
    tpaId: number | string;
    isActive: boolean;
  }) => {
    try {
      const response = await TpaAxiosInstance.post("tpas", payload);

      if (response.data?.success) {
        fetchItems();
        setIsDialogOpen(false);
        clear();
        toast({
          variant: "success",
          title: "Success",
          description: "New TPA Added Successfully.",
        });
      }
    } catch (error: any) {
      const message = getApiErrorMessage(error);
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });
      console.error("Add TPA Error:", error);
    }
  };

  const handleSubmit = async () => {
    setSubmitted(true);
    if (!validateAll()) return;

    const payload = {
      name: values.name.trim(),
      tpaId: values.tpaId,
      isActive: isActive,
    };

    try {
      await addItem(payload);
    } catch (error) {
      const message = getApiErrorMessage(error);
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });
    }
  };

  const handleEdit = (item: Datum) => {
    setField("name", item.name);
    setField("tpaId", item.tpaId);
    setIsActive(item.isActive);
    setEditIndex(item.id);
    setIsDialogOpen(true);
  };

  const updateItem = async (id: string | number) => {
    setSubmitted(true);
    if (!validateAll()) return;

    const payload = {
      name: values.name.trim(),
      tpaId: values.tpaId,
      isActive: isActive,
    };

    try {
      const response = await TpaAxiosInstance.put(`tpas/${id}`, payload);

      if (response.data?.success) {
        fetchItems();
        setIsDialogOpen(false);
        clear();
        toast({
          variant: "success",
          title: "Success",
          description: "TPA Updated Successfully.",
        });
      } else {
        toast({
          title: "Error",
          description: response.data?.message || "Failed to Update the Group.",
          variant: "destructive",
        });
      }
    } catch (error: AxiosError | any) {
      const message = getApiErrorMessage(error);
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });
      console.error(error);
    }
  };

  const removeItem = async (id: string | number): Promise<void> => {
    try {
      await TpaAxiosInstance.delete(`tpas/${id}/soft-delete`);
      fetchItems();
      toast({
        variant: "success",
        title: "Success",
        description: "TPA Deleted Successfully.",
      });
    } catch (error) {
      const message = getApiErrorMessage(error);
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });
    }
  };

  const hardDeleteItem = async (id: string | number): Promise<void> => {
    try {
      await TpaAxiosInstance.delete(`tpas/${id}`);
      await fetchItems();
      toast({
        variant: "success",
        title: "Success",
        description: "Group permanently deleted.",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: getApiErrorMessage(error),
        variant: "destructive",
      });
    }
  };

  const restoreItem = async (id: string | number): Promise<void> => {
    try {
      await TpaAxiosInstance.patch(`tpas/${id}/restore`);
      await fetchItems();
      toast({
        variant: "success",
        title: "Success",
        description: "Group restored successfully.",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: getApiErrorMessage(error),
        variant: "destructive",
      });
    }
  };

  const toggleActive = async (row: Datum) => {
    const payload = {
      name: row.name,
      tpaId: row.tpaId,
      isActive: !row.isActive,
    };

    try {
      await TpaAxiosInstance.put(`tpas/${row.id}`, payload);
      toast({
        variant: "success",
        title: "Success",
        description: `Status Updated to ${!row.isActive ? "Active" : "Inactive"}`,
      });
      setAllItems((prev) =>
        prev.map((item) => (item.id === row.id ? { ...item, isActive: !item.isActive } : item)),
      );
    } catch (error: any) {
      const message = getApiErrorMessage(error);
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });
    }
  };

  // =====================================
  // Debounce
  // =====================================
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    const t = setTimeout(() => {
      setAdvancedSearch({
        name: advancedSearchInput.name.trim(),
        tpaId: (advancedSearchInput.tpaId ?? "").toString().trim(),
        isActive: advancedSearchInput.isActive,
        payers: advancedSearchInput.payers.trim(),
        isSoftDeleted: advancedSearchInput.isSoftDeleted,
      });
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [advancedSearchInput]);

  useEffect(() => {
    fetchItems();
  }, [page, limit, search, advancedSearch]);

  // =====================================
  // Download Function (Backend Export)
  // =====================================
  const handleDownload = async (item: Datum) => {
    if (isViewer()) {
      toast({
        title: "Error",
        description: "Access denied. Viewers are only allowed to view the system",
        variant: "destructive",
      });
      return;
    }

    try {
      const response = await TpaAxiosInstance.get(EXPORT_ROW_URL(item.id), {
        responseType: "blob",
      });
      saveBlobResponse(response, `TPA_${item.tpaId || "export"}.xlsx`);
      toast({
        variant: "success",
        title: "Export Finished",
        description: `"${item.name}" exported successfully.`,
      });
    } catch (error: any) {
      toast({
        title: "Export Failed",
        description: await getBlobErrorMessage(error, "Failed to export TPA."),
        variant: "destructive",
      });
    }
  };

  const highlightMatch = (text: string, query: string) => {
    if (!query || query.trim().length === 0) return text;

    const lowerText = text.toLowerCase();
    const lowerQuery = query.toLowerCase();

    if (!lowerText.includes(lowerQuery)) return text;

    const parts = text.split(new RegExp(`(${query})`, "gi"));

    return parts.map((part, index) =>
      part.toLowerCase() === lowerQuery ? (
        <span
          key={index}
          className="bg-yellow-50 text-yellow-900 px-1 rounded font-semibold"
        >
          {part}
        </span>
      ) : (
        part
      ),
    );
  };

  // Table columns
  const columns: ColumnDef<Datum>[] = [
    {
      accessorKey: "name",
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="w-full font-bold text-black justify-center"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Name
          {column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
        </Button>
      ),
      cell: ({ row }) => {
        const item = row.original;
        return (
          <div data-testid={`tpa-name-${item.id}`} className={`text-center ${item.isSoftDeleted ? "line-through text-slate-400" : ""}`}>
            {row.getValue("name")}
          </div>
        );
      },
    },
    {
      accessorKey: "tpaId",
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="w-full font-bold text-black justify-center"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          TPA ID
          {column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
        </Button>
      ),
      cell: ({ row }) => {
        const item = row.original;
        return (
          <div data-testid={`tpa-id-${item.id}`} className={`text-center ${item.isSoftDeleted ? "line-through text-slate-400" : ""}`}>
            {row.getValue("tpaId")}
          </div>
        );
      },
    },
    {
      accessorKey: "payers",
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="font-bold text-black w-full justify-center"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Payers
          {column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
        </Button>
      ),
      cell: ({ row }) => {
        const rawPayers = row.getValue("payers") as {
          name: string;
          payerId: string;
          isActive?: boolean;
          isSoftDeleted?: boolean;
        }[] || [];

        if (rawPayers.length === 0) {
          return <span className="text-gray-400 text-center block">-</span>;
        }

        const globalSearch = String(search || "").toLowerCase().trim();
        const advancedPayerSearch = String(advancedSearch?.payers || "").toLowerCase().trim();

        const activeSearch = advancedPayerSearch.length > 0
          ? advancedPayerSearch
          : (globalSearch.length > 0 ? globalSearch : "");

        const hasSearch = activeSearch.length > 0;

        const filteredPayers = hasSearch
          ? rawPayers.filter((payer) => (payer.name || "").toLowerCase().includes(activeSearch))
          : rawPayers;

        if (hasSearch && filteredPayers.length === 0) {
          return <span className="text-gray-300 text-center block">No match</span>;
        }

        const displayPayers = filteredPayers.slice(0, 2);
        const remainingCount = filteredPayers.length - 2;

        return (
          <div className="flex flex-col items-center gap-1">
            <div className="flex flex-wrap justify-center items-center gap-1.5">
              {displayPayers.map((payer, idx) => {
                const isDeleted = payer.isSoftDeleted === true;
                const isInactive = payer.isActive === false;
                const name = payer.name || "";

                let badgeStyle = "bg-blue-50 text-blue-800 border-transparent";
                if (isDeleted) {
                  badgeStyle = "bg-red-50 border border-red-200 text-red-500 line-through opacity-60";
                } else if (isInactive) {
                  badgeStyle = "bg-gray-100 border border-gray-300 text-gray-500 opacity-70";
                } else if (hasSearch && name.toLowerCase().includes(activeSearch)) {
                  badgeStyle = "bg-yellow-100 border border-yellow-400 scale-105 text-yellow-950";
                }

                return (
                  <div
                    key={idx}
                    className={`inline-flex items-center gap-1 rounded-full px-2 py-1 transition-all text-xs font-medium border ${badgeStyle}`}
                  >
                    <span>{highlightMatch(name, activeSearch)}</span>
                  </div>
                );
              })}

              {remainingCount > 0 && (
                <button
                  data-testid={`btn-view-more-payers-${row.original.id}`}
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedPayer(filteredPayers);
                    setPayerDialogOpen(true);
                  }}
                  className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-600 text-white hover:bg-blue-700 transition-colors cursor-pointer"
                >
                  +{remainingCount} more
                </button>
              )}
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: "isActive",
      header: () => (
        <div className="w-full font-bold text-black text-center normal-case">
          Status
        </div>
      ),
      cell: ({ row }) => {
        const item = row.original;
        if (item.isSoftDeleted) {
          return (
            <div className="w-full flex justify-center">
              <div className="relative inline-flex h-6 w-11 items-center rounded-full bg-gray-200 opacity-40 cursor-not-allowed">
                <span className="inline-block h-4 w-4 transform rounded-full bg-white translate-x-1" />
              </div>
            </div>
          );
        }
        return (
          <div className="w-full flex justify-center">
            <button
              data-testid={`toggle-active-${item.id}`}
              onClick={() => toggleActive(item)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                item.isActive ? "bg-green-500" : "bg-gray-300"
              }`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                item.isActive ? "translate-x-6" : "translate-x-1"
              }`} />
            </button>
          </div>
        );
      },
    },
    {
      id: "view",
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="w-full font-bold text-black text-center"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          View
          {column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
        </Button>
      ),
      cell: ({ row }) => {
        const item = row.original;
        return (
          <div className="w-full flex justify-center py-2">
            <button
              data-testid={`btn-view-${item.id}`}
              onClick={() => handleOpenDialog(item)}
              className="text-blue-600 hover:text-blue-800"
              title="View Details"
            >
              <i className="fa-solid fa-eye text-lg"></i>
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <div data-testid="TPA-page">
      <div className={styles.container} data-testid="TPA-page-container">
        <h5 className={styles.title} data-testid="TPA-page-title">
          <span className={styles.dialogIcon} data-testid="TPA-page-title-icon">
            <i className="fa-solid fa-building-shield"></i>
          </span>
          TPA
        </h5>

        <FileUpload
          fetchItems={fetchItems}
          totalItems={totalItems}
          hasFilters={hasActiveFilters}
          exportParams={buildFilterParams()}
        />
      </div>

      {/* payerDialog */}
      <Dialog data-testid="TPA-payerDialog" open={payerDialogOpen} onOpenChange={setPayerDialogOpen}>
        <DialogContent className={`${styles["modern-dialog"]} [&>button]:hidden`}>
          <div className={styles["dialog-container"]}>
            <header className={styles["dialog-header"]}>
              <div className={styles["header-content"]}>
                <div className={styles["header-icon"]}>
                  <i className="fa-solid fa-building-shield text-xl"></i>
                </div>
                <div>
                  <h3 className={styles["dialog-title"]} data-testid="TPA-payer-dialog-title">Associated Payers</h3>
                  <p className={styles["dialog-subtitle"]}>List of all payers linked to this group</p>
                </div>
              </div>
              <button
                data-testid="TPA-payer-dialog-btn-close-top"
                onClick={() => setPayerDialogOpen(false)}
                className={styles["close-btn"]}
              >
                <i className="fa-solid fa-xmark"></i>
              </button>
            </header>

            <div className={styles["dialog-body"]}>
              <div className={styles.section}>
                <div className={styles["section-header-flex"]}>
                  <h4 className={styles["section-title"]}>
                    <i className="fa-solid fa-list-ul text-blue-500"></i>
                    Payers Details
                  </h4>
                  <span className={styles["count-badge"]} data-testid="TPA-payer-dialog-total-count">
                    {selectedPayer.length} Total
                  </span>
                </div>

                <div
                  className={`${styles["payer-scroll-area"]} custom-scrollbar`}
                  style={{ scrollbarWidth: "thin", scrollbarColor: "#3b82f6 #f1f5f9" }}
                >
                  <div className={styles["payer-grid"]}>
                    {selectedPayer.map((payer: any, index) => {
                      const isObject = typeof payer === "object" && payer !== null;
                      const payerName = isObject ? (payer.name || "") : String(payer);
                      const isDeleted = isObject && payer.isSoftDeleted === true;
                      const isInactive = isObject && payer.isActive === false;
                      let cardStatusClass = "";
                      let titleTooltip = "";

                      if (isDeleted) {
                        cardStatusClass = "opacity-50 line-through border-red-200 bg-red-50 text-red-500";
                        titleTooltip = "Deleted Payer";
                      } else if (isInactive) {
                        cardStatusClass = "opacity-60 bg-gray-100 text-gray-500 border-gray-300";
                        titleTooltip = "Inactive Payer";
                      }

                      return (
                        <div
                          key={`${isObject ? payer.payerId : payerName}-${index}`}
                          className={`${styles["payer-card-mini"]} ${cardStatusClass} flex items-center justify-between p-2 border rounded-md transition-all`}
                          title={titleTooltip}
                          data-testid={`payer-item-${index}`}
                        >
                          <div className="flex items-center gap-2">
                            <div className={styles["mini-icon"]}>
                              {isDeleted ? (
                                <i className="fa-solid fa-trash-can text-red-500 text-sm"></i>
                              ) : isInactive ? (
                                <i className="fa-solid fa-eye-slash text-gray-400 text-sm"></i>
                              ) : (
                                <i className="fa-solid fa-id-card text-blue-500 text-sm"></i>
                              )}
                            </div>
                            <span className={styles["payer-name"]}>{payerName}</span>
                          </div>
                          <div className="flex gap-1 items-center">
                            {isDeleted && (
                              <span className="text-[9px] font-bold bg-red-100 text-red-600 px-1.5 py-0.5 rounded no-underline">Deleted</span>
                            )}
                            {!isDeleted && isInactive && (
                              <span className="text-[9px] font-bold bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded">Inactive</span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* search */}
      <div className={`${styles["compact-search-container"]} ${styles["mb-6"]}`} data-testid="TPA-page-search-container">
        <div className={styles["search-wrapper"]}>
          <div className={styles["search-header-compact"]}>
            <div className={styles["header-info"]}>
              <i className="fa-solid fa-filter"></i>
              <span>Search Filters</span>
            </div>

            <button
              data-testid="TPA-page-btn-reset-filters"
              onClick={() => {
                setSearchInput("");
                setAdvancedSearchInput({
                  name: "",
                  tpaId: "",
                  isActive: "",
                  payers: "",
                  isSoftDeleted: "false",
                });
              }}
              className={styles["btn-clear-compact"]}
            >
              <i className="fa-solid fa-rotate-left"></i>
              Reset
            </button>
          </div>

          <div className={styles["search-fields-compact"]} style={{ gridTemplateColumns: "repeat(5, 1fr)" }}>
            <div className={styles["field-compact"]} data-testid="TPA-page-search-groupname">
              <label className={styles["label-compact"]}>
                <i className="fa-solid fa-layer-group"></i>
                TPA Name
              </label>
              <div className={styles["input-container-compact"]}>
                <input
                  data-testid="TPA-page-input-search-groupname"
                  type="text"
                  placeholder="Search by TPA Name..."
                  value={advancedSearchInput.name}
                  onChange={(e) => setAdvancedSearchInput({ ...advancedSearchInput, name: e.target.value })}
                  className={styles["input-compact"]}
                />
              </div>
            </div>

            <div className={styles["field-compact"]} data-testid="TPA-page-search-tpaid">
              <label className={styles["label-compact"]}>
                <i className="fa-solid fa-layer-group"></i>
                TPA ID
              </label>
              <div className={styles["input-container-compact"]}>
                <input
                  data-testid="TPA-page-input-search-tpaid"
                  type="text"
                  placeholder="Filter by TPA ID..."
                  value={advancedSearchInput.tpaId ?? ""}
                  onChange={(e) => setAdvancedSearchInput({ ...advancedSearchInput, tpaId: e.target.value })}
                  className={styles["input-compact"]}
                />
              </div>
            </div>

            <div className={styles["field-compact"]} data-testid="Tpa-page-search-Payer">
              <label className={styles["label-compact"]}>
                <i className="fa-solid fa-id-card-clip"></i>
                Payers
              </label>
              <div className={styles["input-container-compact"]}>
                <input
                  data-testid="TPA-page-input-search-payers"
                  type="text"
                  placeholder="Search by payer name..."
                  value={advancedSearchInput.payers}
                  onChange={(e) => setAdvancedSearchInput({ ...advancedSearchInput, payers: e.target.value })}
                  className={styles["input-compact"]}
                />
                {advancedSearchInput.payers && (
                  <button
                    data-testid="TPA-page-btn-clear-payers"
                    className={styles["clear-btn-compact"]}
                    onClick={() => setAdvancedSearchInput({ ...advancedSearchInput, payers: "" })}
                  >
                    <i className="fa-solid fa-xmark"></i>
                  </button>
                )}
              </div>
            </div>

            <div className={styles["field-compact"]} data-testid="TPA-page-search-Status">
              <label className={styles["label-compact"]}>
                <i className="fa-solid fa-toggle-on"></i>
                Status
              </label>
              <div className={styles["select-container-compact"]}>
                <select
                  data-testid="TPA-page-select-search-status"
                  value={advancedSearchInput.isActive}
                  onChange={(e) => setAdvancedSearchInput({ ...advancedSearchInput, isActive: e.target.value })}
                  className={styles["input-compact"]}
                >
                  <option value="">All Status</option>
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>
            </div>

            <div className={styles["field-compact"]} data-testid="TPA-page-search-soft-deleted">
              <label className={styles["label-compact"]}>
                <i className="fa-solid fa-trash-can"></i>
                Deleted TPAs
              </label>
              <button
                data-testid="TPA-page-btn-toggle-soft-deleted"
                type="button"
                onClick={() => setAdvancedSearchInput({
                  ...advancedSearchInput,
                  isSoftDeleted: advancedSearchInput.isSoftDeleted === "true" ? "false" : "true",
                })}
                className={`${styles["input-compact"]} w-full flex items-center justify-center gap-2 cursor-pointer select-none transition-all ${
                  advancedSearchInput.isSoftDeleted === "true"
                    ? "!bg-red-500 !border-red-600 !text-white font-semibold"
                    : "hover:bg-slate-50 text-slate-600"
                }`}
              >
                <i className={`fa-solid ${advancedSearchInput.isSoftDeleted === "true" ? "fa-rotate-left" : "fa-eye-slash"} text-xs`}></i>
                <span className="text-xs">
                  {advancedSearchInput.isSoftDeleted === "true" ? "Back to List" : "Show "}
                </span>
              </button>
            </div>
          </div>

          <div className={styles["stats-compact"]}>
            <i className="fa-solid fa-chart-bar"></i>
            <span className={styles["stats-text-compact"]} data-testid="TPA-page-stats">
              Showing <strong>{allItems.length}</strong> of <strong>{totalItems}</strong> groups
            </span>
            {(advancedSearch.isActive ||
              advancedSearch.isSoftDeleted == "true" ||
              advancedSearch.name ||
              advancedSearch.payers || advancedSearch.tpaId) && (
              <span className={styles["badge-filtered-compact"]}>
                <i className="fa-solid fa-filter"></i> Filtered
              </span>
            )}
          </div>
        </div>
      </div>

      {/* view dialog */}
      {isViewDialogOpen && selectedItem && (
        <dialog className={styles["modern-dialog"]} open data-testid="TPA-page-dialog">
          <div className={styles["dialog-container"]} data-testid="TPA-page-dialog-container">
            <div className={styles["dialog-header"]} data-testid="TPA-page-dialog-header">
              <div className={styles["header-content"]}>
                <div className={styles["header-icon"]} data-testid="TPA-page-dialog-header-icon">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                    <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z" stroke="currentColor" strokeWidth="2" />
                    <path d="M3.27 6.96L12 12.01l8.73-5.05M12 22.08V12" stroke="currentColor" strokeWidth="2" />
                  </svg>
                </div>
                <div>
                  <h2 className={styles["dialog-title"]} data-testid="TPA-page-dialog-title">TPA Details</h2>
                  <p className={styles["dialog-subtitle"]} data-testid="TPA-page-dialog-subtitle">{selectedItem.name}</p>
                </div>
              </div>
              <button className={styles["close-btn"]} onClick={handleCloseDialog} data-testid="TPA-page-dialog-close-btn">✕</button>
            </div>

            <div className={styles["dialog-body"]} data-testid="TPA-page-dialog-body">
              <section className={styles.section} data-testid="TPA-page-dialog-basic-info">
                <h3 className={styles["section-title"]}>Basic Information</h3>
                <div className={styles["info-group"]}>
                  <div className={styles["info-row"]} data-testid="TPA-page-dialog-row-id">
                    <span className={styles["row-label"]}>TPA ID</span>
                    <span className={styles["info-text"]}>{selectedItem.tpaId}</span>
                  </div>
                  <div className={styles["info-row"]} data-testid="TPA-page-dialog-row-name">
                    <span className={styles["row-label"]}>TPA Name</span>
                    <span className={styles["info-text"]}>{selectedItem.name}</span>
                  </div>
                  <div className={styles["info-row"]}>
                    <span className={styles["row-label"]}>Payers</span>
                    <div className="flex flex-wrap gap-3">
                      {selectedItem.payers?.length
                        ? selectedItem.payers.map((group, idx) => {
                            const softDeleteClasses = group.isSoftDeleted
                              ? "opacity-35 blur-[0.4px] line-through decoration-red-400 border-dashed bg-red-50 text-red-500"
                              : !group.isActive
                              ? styles["badge-inactive"]
                              : styles["badge-active"];

                            return (
                              <div key={idx} className={`${styles["group-badge-v2"]} ${softDeleteClasses}`}>
                                {group.isSoftDeleted ? (
                                  <i className="fa-solid fa-trash-can mr-2 text-xs text-red-500"></i>
                                ) : !group.isActive ? (
                                  <i className="fa-solid fa-eye-slash mr-2 text-xs"></i>
                                ) : null}
                                <span className={styles["group-name"]}>{group.name}</span>
                                {group.isSoftDeleted ? (
                                  <span className="text-[9px] font-bold bg-red-100 text-red-600 px-1 rounded ml-1.5 tracking-wide">Deleted</span>
                                ) : !group.isActive ? (
                                  <span className={styles["status-text-mini"]}>Inactive</span>
                                ) : null}
                              </div>
                            );
                          })
                        : "—"}
                    </div>
                  </div>
                  <div className={styles["info-row"]} data-testid="TPA-page-dialog-row-status">
                    <span className={styles["row-label"]}>Status</span>
                    <div className={`${styles["status-badge-inline"]} ${selectedItem.isActive ? styles.active : styles.inactive}`} data-testid="TPA-page-dialog-status-badge">
                      <span className={styles["status-dot"]}></span>
                      {selectedItem.isActive ? "Active" : "Inactive"}
                    </div>
                  </div>
                </div>
              </section>

              <div style={{ padding: "20px 28px 8px" }} data-testid="TPA-page-dialog-audit-trail">
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                  <div style={{ width: 28, height: 28, borderRadius: 8, background: "#f0fdf4", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #bbf7d0" }}>
                    <i className="fa-solid fa-clock-rotate-left" style={{ fontSize: 12, color: "#16a34a" }} />
                  </div>
                  <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Audit Trail</span>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 16 }}>
                  <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px" }}>
                    <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8" }}>Created By</span>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10 }}>
                      <div style={{ width: 32, height: 32, borderRadius: "50%", background: "linear-gradient(135deg, #3b82f6, #2563eb)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, color: "#fff" }}>
                        {String(getCreatorName(selectedItem) ?? "?")[0].toUpperCase()}
                      </div>
                      <span style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>{getCreatorName(selectedItem) || "—"}</span>
                    </div>
                  </div>

                  <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px" }}>
                    <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8" }}>Created On</span>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
                      <i className="fa-regular fa-calendar" style={{ fontSize: 14, color: "#3b82f6" }} />
                      <span style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>
                        {(selectedItem as any).createdAt ? new Date((selectedItem as any).createdAt).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
                      </span>
                    </div>
                  </div>

                  <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px" }}>
                    <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8" }}>Last Updated</span>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
                      <i className="fa-regular fa-clock" style={{ fontSize: 14, color: "#10b981" }} />
                      <span style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>
                        {(selectedItem as any).updatedAt ? new Date((selectedItem as any).updatedAt).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
                      </span>
                    </div>
                  </div>
                </div>

                {(selectedItem as any).auditLog?.length > 0 && (
                  <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 12 }}>
                      <i className="fa-solid fa-pen-to-square" style={{ fontSize: 11, color: "#10b981" }} />
                      <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase" }}>Update History</span>
                      <span style={{ fontSize: 10, padding: "1px 8px", borderRadius: 9999, background: "#f0fdf4", color: "#15803d", fontWeight: 600, border: "1px solid #bbf7d0", marginLeft: "auto" }}>
                        {(selectedItem as any).auditLog.length} update{(selectedItem as any).auditLog.length !== 1 ? "s" : ""}
                      </span>
                    </div>

                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      {(selectedItem as any).auditLog.slice(0, showAllLogs ? undefined : 4).map((log: any, i: number) => (
                        <div key={log.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", borderRadius: 8, background: i === 0 ? "#f0fdf4" : "#f8fafc", border: `1px solid ${i === 0 ? "#bbf7d0" : "#e2e8f0"}` }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <div style={{ width: 28, height: 28, borderRadius: "50%", background: i === 0 ? "linear-gradient(135deg, #10b981, #059669)" : "linear-gradient(135deg, #94a3b8, #64748b)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: "#fff" }}>
                              {String(log.actorName ?? "?")[0].toUpperCase()}
                            </div>
                            <span style={{ fontSize: 12, fontWeight: 600, color: "#1e293b" }}>{log.actorName}</span>
                            {i === 0 && (
                              <span style={{ fontSize: 10, padding: "1px 8px", borderRadius: 9999, background: "#dcfce7", color: "#15803d", fontWeight: 600, border: "1px solid #bbf7d0" }}>Latest</span>
                            )}
                          </div>
                          <span style={{ fontSize: 11, color: "#64748b", fontWeight: 500 }}>
                            {new Date(log.createdAt).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                          </span>
                        </div>
                      ))}

                      {(selectedItem as any).auditLog.length > 4 && (
                        <div style={{ textAlign: "center", padding: "6px 0" }}>
                          <button
                            data-testid="TPA-page-dialog-btn-toggle-logs"
                            onClick={() => setShowAllLogs((v) => !v)}
                            style={{ fontSize: 11, color: "#3b82f6", fontWeight: 600, background: "none", border: "none", cursor: "pointer" }}
                          >
                            {showAllLogs ? "Show less ↑" : `+${(selectedItem as any).auditLog.length - 4} more updates ↓`}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className={styles["dialog-footer"]} data-testid="TPA-page-dialog-footer">
              <button className={styles["btn-secondary"]} onClick={handleCloseDialog} data-testid="TPA-page-dialog-close-btn">
                Close
              </button>
            </div>
          </div>
        </dialog>
      )}

      {/* delete dialog */}
      <Dialog
        data-testid="TPA-deletedialog"
        open={warningDialog.open}
        onOpenChange={(open) => !open && setWarningDialog({ open: false, type: null, item: null, usageData: null, loadingUsage: false })}
      >
        <DialogContent className="max-w-md bg-white p-0 overflow-hidden rounded-2xl flex flex-col max-h-[90vh]" data-testid="page-delete-warning-dialog-content">
          <div className="p-6 pb-2 flex-shrink-0">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 font-bold text-red-600">
                <AlertTriangle className="h-5 w-5" />
                {warningDialog.type === "hardDelete" ? "Permanent Deletion" : "Confirm Deletion"}
              </DialogTitle>
              <DialogDescription className="text-slate-500 text-sm mt-1">
                {warningDialog.type === "hardDelete"
                  ? "This will permanently remove the item from the system. This action cannot be undone."
                  : "Are you sure you want to delete this item? This action cannot be undone."}
              </DialogDescription>
            </DialogHeader>
          </div>

          <div className="px-6 flex-1 min-h-0 flex flex-col overflow-y-auto">
            {warningDialog.type === "hardDelete" && (
              <div className="py-6 flex flex-col items-center justify-center gap-3">
                <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center border-2 border-red-100">
                  <i className="fa-solid fa-trash-can text-red-500 text-xl"></i>
                </div>
                <p className="text-sm text-slate-600 text-center px-4">
                  The item <strong className="text-slate-800">"{warningDialog.item?.name || "this item"}"</strong> will be permanently deleted.
                </p>
              </div>
            )}

            {warningDialog.type === "delete" && (
              <div className="py-10 text-center flex flex-col items-center justify-center">
                <div className="bg-slate-50 p-4 rounded-full mb-4">
                  <AlertTriangle className="h-8 w-8 text-slate-300" />
                </div>
                <p className="text-slate-600 text-sm px-4">
                  Are you sure you want to delete <strong className="text-slate-800">"{warningDialog.item?.name || "this item"}"</strong>? This item will be soft-deleted and can be restored later.
                </p>
              </div>
            )}
          </div>

          <div className="p-6 pt-4 flex-shrink-0 bg-white border-t mt-auto">
            <DialogFooter className="flex flex-row gap-3 justify-center">
              <Button
                data-testid="warning-dialog-btn-cancel"
                variant="outline"
                className="flex-1 rounded-xl h-10 text-sm"
                onClick={() => setWarningDialog((prev) => ({ ...prev, open: false }))}
              >
                Cancel
              </Button>
              <Button
                data-testid="warning-dialog-btn-confirm"
                className="flex-1 rounded-xl h-10 text-white font-bold text-sm bg-red-500 hover:bg-red-600"
                onClick={async () => {
                  const itemId = warningDialog.item?.id;
                  if (itemId) {
                    if (warningDialog.type === "hardDelete") {
                      await hardDeleteItem(itemId);
                    } else {
                      await removeItem(itemId);
                    }
                  }
                  setWarningDialog((prev) => ({ ...prev, open: false }));
                }}
              >
                {warningDialog.type === "hardDelete" ? "Yes, Permanently Delete" : "Yes, Delete"}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={isDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            clear();
            setEditIndex(null);
          }
          setIsDialogOpen(open);
        }}
      >
        <DialogContent className={styles.dialogContent} data-testid="TPA-page-dialogContent">
          <DialogHeader className={styles.dialogHeader}>
            <DialogTitle className={styles.dialogTitle} data-testid="TPA-form-dialog-title">
              {editIndex === null ? "Add TPA " : "Edit TPA "}
            </DialogTitle>
            <DialogDescription className={styles.dialogDescription} data-testid="TPA-page-dialog-description">
              Fill in the details below to {editIndex === null ? "create" : "update"} a TPA .
            </DialogDescription>
          </DialogHeader>

          <div className={styles.dialogBody} data-testid="TPA-page-dialog-body">
            <div className={styles.formGrid}>
              <div>
                <div className={styles.formGroup} data-testid="TPA-page-input-groupname">
                  <Label htmlFor="name">Name :</Label>
                  <Input
                    data-testid="TPA-form-input-name"
                    placeholder="Enter Name..."
                    value={values.name}
                    onChange={(e) => setField("name", e.target.value)}
                  />
                  {submitted && errors.name && (
                    <span className={styles.error} data-testid="TPA-form-error-name">{errors.name}</span>
                  )}
                </div>
              </div>

              <div>
                <div className={styles.formGroup} data-testid="TPA-page-input-tpaid">
                  <Label htmlFor="TpaId">TPA ID: </Label>
                  <Input
                    data-testid="TPA-form-input-tpaid"
                    type="text"
                    placeholder="Enter TPA ID..."
                    value={values.tpaId || ""}
                    onChange={(e) => setField("tpaId", e.target.value)}
                  />
                  {submitted && errors.tpaId && (
                    <span className={styles.error} data-testid="TPA-form-error-tpaid">{errors.tpaId}</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className={styles.dialogFooter}>
            <Button
              data-testid="TPA-form-btn-submit"
              onClick={() => {
                if (editIndex === null) {
                  handleSubmit();
                } else {
                  updateItem(editIndex);
                }
              }}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {editIndex === null ? "Add TPA" : "Update TPA"}
            </Button>

            <Button
              data-testid="TPA-form-btn-cancel"
              className={styles.cancel}
              variant="outline"
              onClick={() => {
                setIsDialogOpen(false);
                setEditIndex(null);
                setSubmitted(false);
                clear();
              }}
            >
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Toaster data-testid="TPA-page-toaster" />

      <div data-testid="TPA-page-table-wrapper">
        <DataTable
          onAdd={handleAdd}
          onEdit={(item: any) => {
            if (item.isSoftDeleted) return;
            handleEdit(item);
          }}
          onDelete={(id) => {
            const item = allItems.find((i) => i.id === id) ?? null;
            if (item?.isSoftDeleted) return;
            setWarningDialog({
              open: true,
              type: "delete",
              item,
              usageData: null,
              loadingUsage: false,
            });
          }}
          data={allItems}
          columns={columns}
          onDownload={handleDownload}
          addButtonLabel="Add New TPA"
          pageIndex={page - 1}
          pageSize={limit}
          totalPages={totalPages}
          onPageChange={(newPage) => setPage(newPage + 1)}
          onPageSizeChange={(size) => {
            setLimit(size);
            setPage(1);
          }}
          globalSearch={searchInput}
          onGlobalSearchChange={setSearchInput}
          recoveryActions={(item: any) => {
            if (!canRestoreOrHardDelete()) {
              return (
                <span className="inline-flex items-center gap-1 text-[10px] font-bold text-slate-400 bg-slate-50 border border-slate-200 px-2 py-1 rounded-lg">
                  <i className="fa-solid fa-lock text-[15px]"></i>
                </span>
              );
            }
            return (
              <div className="flex items-center justify-center gap-1.5">
                <button
                  data-testid={`btn-restore-${item.id}`}
                  onClick={() => restoreItem(item.id)}
                  className="flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 transition-all"
                >
                  <i className="fa-solid fa-rotate-left text-[9px]"></i>
                  Restore
                </button>
                <button
                  data-testid={`btn-hard-delete-${item.id}`}
                  onClick={() => setWarningDialog({
                    open: true,
                    type: "hardDelete",
                    item,
                    usageData: null,
                    loadingUsage: false,
                  })}
                  className="flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-bold bg-red-50 text-red-600 border border-red-200 hover:bg-red-100 transition-all"
                >
                  <i className="fa-solid fa-trash text-[9px]"></i>
                  Delete
                </button>
              </div>
            );
          }}
          data-testid="TPA-page-datatable"
        />
      </div>
    </div>
  );
};

export default TpaPage;




